//--------------------------------------------------------------------//
//******************** trunkNumbersTab *********************************//
//--------------------------------------------------------------------//
// Manages the DID-number-to-GudHub-user mapping (trunk.did_numbers) for a
// trunk. did_numbers is a single embedded array field on the trunk
// document (no separate endpoints), so every add/edit/remove re-submits
// the whole array via SipTrunksService.update. Add and Edit share the same
// dialog (openNumberDialog) — Edit is opened by clicking a row.
import trunkNumbersTemplateUrl from './trunk-numbers.template.html';
import './voicemail-greeting-recorder/voicemail-greeting-recorder.js';

angular.module('sipTrunksTableModule')

.directive('trunkNumbersTab', ['SipTrunksService', 'GhDialog', function (SipTrunksService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            trunk: '=',
            onUpdate: '&'
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            if (!Array.isArray($scope.trunk.did_numbers)) {
                $scope.trunk.did_numbers = [];
            }

            // gh-user-selecting reads ghField.data_model directly (no optional
            // chaining internally) — a stable stub avoids both a runtime error
            // and a fresh-object-every-digest infinite $digest loop that an
            // inline `{data_model:{}}` template literal would cause.
            $scope.ghFieldStub = { data_model: { photo_size: '32px' } };

            const safeApply = function () {
                $scope.$applyAsync();
            };

            async function persistDidNumbers(didNumbers) {
                const updated = await SipTrunksService.update($scope.trunk.trunk_id, {
                    did_numbers: didNumbers
                });
                Object.assign($scope.trunk, updated);
                safeApply();
                $scope.onUpdate();
            }

            // Opens a dialog to add a new DID number, or edit an existing one
            // when `existingDid` (the exact array item reference) is passed.
            function openNumberDialog(existingDid) {
                const existingDidNumbers = $scope.trunk.did_numbers;

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="add_did_number-popup" style="padding: 20px; position: relative; min-width: 380px;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="confirmCancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #333;">{{isEditing ? translate('Edit DID Number') : translate('Add DID Number')}}</h3>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Phone number')}}</label>
                                <input type="text" ng-model="number" style="width: 100%; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 16px; outline: none; margin-bottom: 16px;">

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('GudHub user')}}</label>
                                <div class="did_number-user_picker" style="margin-bottom: 16px;">
                                    <gh-user-selecting gh-model="gudhubUserId" gh-field="ghFieldStub" gh-mode="little_photo" gh-dropdown="" editable="true"></gh-user-selecting>
                                </div>

                                <voicemail-greeting-recorder
                                    ng-if="isEditing"
                                    trunk-id="trunkId"
                                    number="existingNumber"
                                    did-entry="existingDidRef"
                                    style="display: block; margin-bottom: 16px;"
                                ></voicemail-greeting-recorder>
                                <p ng-if="!isEditing" style="font-size: 13px; color: #999; margin-bottom: 16px;">{{translate('Save this number to enable a voicemail greeting.')}}</p>

                                <div ng-show="errMessage" style="color: #f44336; font-size: 13px; margin-bottom: 12px;">{{errMessage}}</div>

                                <div style="display: flex; gap: 10px; justify-content: center;">
                                    <button class="btn btn-blue-reverse" ng-click="confirmCancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-blue" ng-click="save()" ng-disabled="saving">{{isEditing ? translate('Save') : translate('Add')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        existingDidNumbers,
                        existingDid: existingDid || null,
                        trunkId: $scope.trunk.trunk_id
                    },
                    controller: ['$scope', 'existingDidNumbers', 'existingDid', 'trunkId', 'GhDialog', 'localizationService', function ($scope, existingDidNumbers, existingDid, trunkId, GhDialog, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);
                        $scope.isEditing = !!existingDid;
                        $scope.number = existingDid ? existingDid.number : '';
                        $scope.gudhubUserId = existingDid ? existingDid.gudhub_user_id : '';
                        // Bound into <voicemail-greeting-recorder> below - kept
                        // separate from $scope.number (the editable phone-field
                        // input above) since the greeting endpoints always target
                        // the number as it exists on the server right now, not
                        // whatever's currently typed but unsaved.
                        $scope.trunkId = trunkId;
                        $scope.existingNumber = existingDid ? existingDid.number : '';
                        $scope.existingDidRef = existingDid;
                        $scope.ghFieldStub = { data_model: { photo_size: '48px' } };
                        $scope.errMessage = '';
                        $scope.saving = false;

                        // gh-user-selecting's own search input only hides itself once
                        // ghField.field_value is truthy — a separate signal from
                        // ghModel, normally kept in sync by whatever real GudHub field
                        // wraps it. Our stub has no such field, so without this watch
                        // the search box (and its own stray "clear" icon) stay stuck
                        // visible underneath the resolved user photo/name.
                        $scope.$watch('gudhubUserId', function (value) {
                            $scope.ghFieldStub.field_value = value || null;
                        });

                        // Guards against an accidental close (X icon or
                        // Cancel button) losing whatever's been typed/
                        // recorded but not saved yet - styled like the
                        // existing "Delete Number" confirm dialog.
                        $scope.confirmCancel = function () {
                            GhDialog.show({
                                position: 'center',
                                toolbar: false,
                                template: {
                                    toolbar: '',
                                    resizable: false,
                                    content: `
                                        <div style="padding: 20px; position: relative; min-width: 360px; text-align: center;">
                                            <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                            <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 12px; color: #333;">{{translate('Discard Changes')}}</h3>
                                            <p style="font-size: 14px; color: #666; margin-bottom: 20px;">{{translate('Are you sure that you want to cancel your changes?')}}</p>
                                            <div style="display: flex; gap: 10px; justify-content: center;">
                                                <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Keep Editing')}}</button>
                                                <button class="btn btn-red" ng-click="confirm()">{{translate('Discard')}}</button>
                                            </div>
                                        </div>
                                    `
                                },
                                controller: ['$scope', function (confirmScope) {
                                    confirmScope.translate = $scope.translate;

                                    confirmScope.confirm = function () {
                                        confirmScope.hide();
                                        $scope.cancel();
                                    };
                                }]
                            });
                        };

                        $scope.save = async function () {
                            const number = ($scope.number || '').trim();
                            const gudhubUserId = $scope.gudhubUserId ? String($scope.gudhubUserId).trim() : '';

                            if (!number || !gudhubUserId) {
                                $scope.errMessage = 'Please, fill in both fields';
                                return;
                            }

                            $scope.saving = true;

                            try {
                                const entry = { number, gudhub_user_id: gudhubUserId };
                                // did_numbers is replaced wholesale by this PUT (see
                                // SipTrunksService.update above), so an entry that
                                // doesn't carry voice_mail_greeting forward would
                                // silently wipe out a recorded greeting server-side -
                                // existingDid is the same object <voicemail-greeting-
                                // recorder> mutates directly (see existingDidRef
                                // above), so this picks up a just-recorded greeting too.
                                if (existingDid && existingDid.voice_mail_greeting) {
                                    entry.voice_mail_greeting = existingDid.voice_mail_greeting;
                                }

                                const updatedList = existingDid
                                    ? existingDidNumbers.map((d) => d === existingDid ? entry : d)
                                    : [...existingDidNumbers, entry];

                                await persistDidNumbers(updatedList);
                                $scope.hide();
                            } catch (error) {
                                console.log('Save DID number error:', error);
                                $scope.errMessage = error.message || 'Failed to save number';
                                $scope.saving = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                });
            }

            $scope.addNumber = function () {
                openNumberDialog(null);
            };

            $scope.editNumber = function (did) {
                openNumberDialog(did);
            };

            // Shows a confirmation dialog and removes the DID number on confirm.
            $scope.deleteNumber = function (did) {
                const remaining = $scope.trunk.did_numbers.filter((d) => d !== did);

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div style="padding: 20px; position: relative; min-width: 360px; text-align: center;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 12px; color: #333;">{{translate('Delete Number')}}</h3>
                                <p style="font-size: 14px; color: #666; margin-bottom: 20px;">{{translate('Are you sure you want to delete')}} <strong>{{didNumber}}</strong>?</p>
                                <div style="display: flex; gap: 10px; justify-content: center;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-red" ng-click="confirm()" ng-disabled="deleting">{{translate('Delete')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        didNumber: did.number
                    },
                    controller: ['$scope', 'didNumber', 'localizationService', function ($scope, didNumber, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);
                        $scope.didNumber = didNumber;
                        $scope.deleting = false;

                        $scope.confirm = async function () {
                            $scope.deleting = true;

                            try {
                                await persistDidNumbers(remaining);
                                $scope.hide();
                            } catch (error) {
                                console.log('Delete DID number error:', error);
                                $scope.deleting = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                });
            };
        }],
        templateUrl: trunkNumbersTemplateUrl
    };
}]);
